// Arabic Resources hub, Blog index + post, Podcast, Careers.
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Mic, FolderOpen, Download, Briefcase, MapPin, Clock } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ArPageHero, ArBlock, ArBullets, ArContactCTA } from "@/pages/ar/_ArShared";
import { api } from "@/lib/api";
import NotFound from "@/pages/NotFound";
import { BLOG_INTRO_AR, BLOG_POSTS_AR, getBlogPostAr } from "@/data/blog-ar";
import { PODCAST_INTRO_AR, PODCAST_EPISODES_AR } from "@/data/podcast-ar";

const NAV_CARDS = [
  { icon: BookOpen, title: "المدونة", desc: "ملاحظات عن قانون حماية البيانات، الذكاء الاصطناعي، شراء البرمجيات، وتشغيل المنصات في عُمان.", to: "/ar/resources/blog" },
  { icon: Mic, title: "البودكاست", desc: "أحاديث مع مشغّلين ومهندسين ومشترين.", to: "/ar/resources/podcast" },
  { icon: FolderOpen, title: "دراسات الحالة", desc: "أنظمة لنا تعمل أو على وشك الإطلاق. موثّقة بشكل صحيح.", to: "/ar/work" },
  { icon: Download, title: "التنزيلات", desc: "قوائم تحقق وقوالب يمكنكم تشغيلها.", to: "/ar/resources/downloads" },
];

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("ar", { day: "numeric", month: "long", year: "numeric" }); }
  catch (_) { return iso; }
}

export function ResourcesIndexAr() {
  return (
    <div data-testid="resources-index-ar">
      <Seo title="الموارد" description="مدونة، بودكاست، ودراسات حالة من وفا سوليوشنز." />
      <ArPageHero
        eyebrow="الموارد"
        h1="الموارد"
        sub="ما تعلمناه من بناء وتشغيل برمجيات في عُمان. مكتوب لمن يتخذ القرارات، لا لمحركات البحث."
        breadcrumbs={[{ label: "الرئيسية", to: "/ar" }, { label: "الموارد" }]}
      />
      <ArBlock tint="white">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {NAV_CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.to} to={c.to} data-testid={`resources-nav-ar-${c.title}`} className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]">
                <Icon className="h-6 w-6 text-wafa-primary" />
                <div className="mt-4 font-heading text-lg font-semibold text-wafa-deep group-hover:text-wafa-primary">{c.title}</div>
                <p className="mt-2 text-sm text-wafa-ink/70">{c.desc}</p>
                <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-wafa-deep">
                  اذهبوا هناك <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                </div>
              </Link>
            );
          })}
        </div>
      </ArBlock>
      <ArBlock tint="cream" heading="أحدث المقالات">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {BLOG_POSTS_AR.slice(0, 3).map((p) => (
            <Link key={p.slug} to={`/ar/resources/blog/${p.slug}`} className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-wafa-primary">{p.tag}</div>
              <div className="mt-3 font-heading text-lg font-semibold text-wafa-deep group-hover:text-wafa-primary">{p.title}</div>
              <p className="mt-3 text-sm text-wafa-ink/70 line-clamp-3">{p.dek}</p>
              <div className="mt-6 flex items-center justify-between text-xs text-wafa-ink/60">
                <span>{formatDate(p.date)}</span>
                <span>{p.readTime}</span>
              </div>
            </Link>
          ))}
        </div>
      </ArBlock>
      <ArContactCTA heading="تريدون أن نكتب عن شيء محدد؟" />
    </div>
  );
}

export function BlogIndexAr() {
  return (
    <div data-testid="blog-index-ar">
      <Seo title="المدونة" description="ملاحظات عن تشغيل أعمال البرمجيات في عُمان — من مهندسينا ومشغّلينا." />
      <ArPageHero
        eyebrow="المدونة"
        h1="مدونة وفا سوليوشنز"
        sub={BLOG_INTRO_AR}
        breadcrumbs={[{ label: "الرئيسية", to: "/ar" }, { label: "الموارد", to: "/ar/resources" }, { label: "المدونة" }]}
      />
      <ArBlock tint="white">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {BLOG_POSTS_AR.map((p) => (
            <Link key={p.slug} to={`/ar/resources/blog/${p.slug}`} data-testid={`blog-card-ar-${p.slug}`} className="group flex flex-col overflow-hidden rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-wafa-primary">{p.tag}</div>
              <div className="mt-3 font-heading text-lg font-semibold text-wafa-deep group-hover:text-wafa-primary">{p.title}</div>
              <p className="mt-3 text-sm text-wafa-ink/70 line-clamp-4">{p.dek}</p>
              <div className="mt-6 flex items-center justify-between text-xs text-wafa-ink/60">
                <span>{formatDate(p.date)}</span>
                <span>{p.readTime}</span>
              </div>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-wafa-deep">
                اقرأوا <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </ArBlock>
    </div>
  );
}

export function BlogPostAr() {
  const { slug } = useParams();
  const post = getBlogPostAr(slug);
  if (!post) return <NotFound />;
  return (
    <div data-testid="blog-post-ar">
      <Seo title={post.title} description={post.dek} />
      <ArPageHero
        eyebrow={post.tag}
        h1={post.title}
        sub={post.dek}
        breadcrumbs={[{ label: "الرئيسية", to: "/ar" }, { label: "المدونة", to: "/ar/resources/blog" }, { label: post.title }]}
      />
      <ArBlock tint="white">
        <div className="mx-auto max-w-3xl">
          <div className="mb-8 text-xs text-wafa-ink/60">{formatDate(post.date)} · {post.readTime}</div>
          {post.inShort ? (
            <div className="rounded-lg border-s-4 border-wafa-primary bg-wafa-cream/60 p-6 mb-10">
              <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary mb-3">باختصار</div>
              <ArBullets items={post.inShort} />
            </div>
          ) : null}
          {post.intro ? <p className="text-base leading-relaxed text-wafa-ink/85 lg:text-lg">{post.intro}</p> : null}
          <div className="mt-8 space-y-8">
            {post.sections.map((s, i) => (
              <section key={i}>
                <h2 className="font-heading text-2xl font-semibold text-wafa-deep">{s.h2}</h2>
                <p className="mt-4 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">{s.body}</p>
              </section>
            ))}
          </div>
        </div>
      </ArBlock>
      <ArContactCTA heading="جاهزون للحديث؟" />
    </div>
  );
}

export function PodcastAr() {
  return (
    <div data-testid="podcast-ar">
      <Seo title="بُني في عُمان — بودكاست وفا" description="أحاديث مع من يبنون الأشياء هنا." />
      <ArPageHero
        eyebrow="البودكاست"
        h1="بُني في عُمان"
        sub={PODCAST_INTRO_AR}
        breadcrumbs={[{ label: "الرئيسية", to: "/ar" }, { label: "الموارد", to: "/ar/resources" }, { label: "البودكاست" }]}
      />
      <ArBlock tint="white">
        <ol className="space-y-4">
          {PODCAST_EPISODES_AR.map((ep) => (
            <li key={ep.slug} className="rounded-lg border border-wafa-deep/10 bg-white p-6">
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-wafa-cream font-heading text-lg font-semibold text-wafa-deep">
                  {String(ep.number).padStart(2, "0")}
                </div>
                <div className="flex-1">
                  <span className="rounded-full bg-wafa-sunshine px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-wafa-deep">{ep.status}</span>
                  <h2 className="mt-2 font-heading text-lg font-semibold text-wafa-deep sm:text-xl">{ep.title}</h2>
                  <p className="mt-2 text-sm text-wafa-ink/75 lg:text-base">{ep.summary}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </ArBlock>
      <ArContactCTA heading="هل ستكون ضيفًا جيدًا؟" />
    </div>
  );
}

const PERKS_AR = [
  { title: "تواصل مباشر مع العملاء من الشهر الأول", desc: "" },
  { title: "تقنية حديثة بلا سجن الأنظمة القديمة", desc: "" },
  { title: "أدوات ذكاء اصطناعي كمعيار قياسي", desc: "" },
  { title: "ساعات مرنة عبر مناطق زمنية", desc: "" },
  { title: "ملكية حقيقية لوحدة، لا لمهمة", desc: "" },
];

export function CareersAr() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/jobs").then(({ data }) => setJobs(data.items || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div data-testid="careers-page-ar">
      <Seo title="الوظائف — وفا سوليوشنز" description="انضموا إلى فريق صغير ومتمرس يبني ويشغّل منتجات حقيقية." />
      <ArPageHero
        eyebrow="الوظائف"
        h1="ابنوا أشياء تعمل في العالم الحقيقي."
        breadcrumbs={[{ label: "الرئيسية", to: "/ar" }, { label: "الشركة", to: "/ar/company" }, { label: "الوظائف" }]}
      />
      <ArBlock tint="white" heading="فريق صغير. منتجات حقيقية. بلا طبقات إدارية.">
        <p className="max-w-3xl text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          مهندسونا يتحدثون مباشرة مع العملاء، يتخذون قرارات البنية، ويرون كودهم يحمل طلبات حقيقية خلال أسبوع من كتابته. إن كنتم تريدون أن تكونوا واحدًا من أربعين شخصًا على قائمة تذاكر، هذا ليس المكان المناسب.
        </p>
      </ArBlock>
      <ArBlock tint="cream" heading="ما تحصلون عليه">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {PERKS_AR.map((p, i) => (
            <div key={i} className="rounded-lg border border-wafa-deep/10 bg-white p-5">
              <div className="font-heading font-semibold text-wafa-deep">{p.title}</div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-wafa-ink/75">
          ندعم التعمين. نوظّف ونُدرّب المواطنين العُمانيين، ويسعدنا الحديث عن وضعنا الحالي بصراحة.
        </p>
      </ArBlock>
      <ArBlock tint="white" heading="الوظائف المفتوحة">
        {loading ? (
          <div className="text-sm text-wafa-ink/60">جارٍ التحميل…</div>
        ) : jobs.length === 0 ? (
          <div className="rounded-lg border border-wafa-deep/10 bg-wafa-cream/40 p-6" data-testid="careers-empty-ar">
            <p className="text-sm text-wafa-ink/85 lg:text-base">
              <span className="font-semibold text-wafa-deep">لا توجد وظائف مفتوحة حاليًا</span> — أرسلوا لنا شيئًا بنيتموه على أي حال. نحتفظ بالسير الذاتية الجيدة.
            </p>
            <p className="mt-4 text-sm text-wafa-ink/75">أرسلوا سيرتكم إلى <a href="mailto:support@wafa.om" className="underline">support@wafa.om</a>.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {jobs.map((j) => (
              <Link key={j.id} to={`/ar/company/careers/${j.slug}`} className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-wafa-primary">
                  <Briefcase className="h-3.5 w-3.5" /> {j.department}
                </div>
                <div className="mt-3 font-heading text-xl font-semibold text-wafa-deep sm:text-2xl">{j.title}</div>
                <p className="mt-3 text-sm text-wafa-ink/75 lg:text-base">{j.summary}</p>
                <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-wafa-ink/70">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{j.location}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{j.employment_type}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </ArBlock>
    </div>
  );
}
